/**
 * Per-app puzzle coin keys on GET /home/user `cloudUser.puzzleCoins`.
 */
export const PUZZLE_COINS_APP = {
    SLIDING_PUZZLE_QUEST: "sliding_puzzle_quest",
    JIGSAW_MUSE: "jigsaw_muse",
    WORD_WHEEL_QUEST: "word_wheel_quest",
    /** Web Stripe purchase / hint ledger (`PayPlanAppCode.WEB_APP`). */
    WEB: "web_app",
    /** @deprecated Prefer {@link #WEB}; backend may still send `purchase`. */
    PURCHASE: "purchase",
    /** Sum of {@code HINT} ledger rows (web hint spends / grants). Prefer {@link #WEB_HINT}. */
    HINT: "hint",
    /** Profile / API key for web hint puzzle coins. */
    WEB_HINT: "web_hint",
    TOTAL: "total",
};

/** @deprecated Backend rollout — prefer `jigsaw_muse`. */
const LEGACY_JIGSAW_COINS_KEY = "jigsaw_puzzle_master";

function readCoinsMap(cloudUser) {
    const map = cloudUser?.puzzleCoins;
    return map && typeof map === "object" ? map : null;
}

function readCoinsValue(map, key) {
    if (!map || key == null) return 0;
    const n = Number(map[key]);
    return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Allows negative balances (e.g. HINT spend ledger). */
function readCoinsSigned(map, key) {
    if (!map || key == null) return 0;
    const n = Number(map[key]);
    return Number.isFinite(n) ? n : 0;
}

/**
 * Jigsaw Muse lifetime coins from profile.
 * @param {object | null | undefined} cloudUser
 * @returns {number}
 */
export function resolveJigsawMuseCoins(cloudUser) {
    const map = readCoinsMap(cloudUser);
    if (!map) return 0;
    if (map[PUZZLE_COINS_APP.JIGSAW_MUSE] != null) {
        return readCoinsValue(map, PUZZLE_COINS_APP.JIGSAW_MUSE);
    }
    return readCoinsValue(map, LEGACY_JIGSAW_COINS_KEY);
}

/**
 * Sliding Puzzle Quest lifetime coins from profile.
 * @param {object | null | undefined} cloudUser
 * @returns {number}
 */
export function resolveSlidingQuestCoins(cloudUser) {
    return readCoinsValue(readCoinsMap(cloudUser), PUZZLE_COINS_APP.SLIDING_PUZZLE_QUEST);
}

/**
 * Word Wheel Quest lifetime coins from profile.
 * @param {object | null | undefined} cloudUser
 * @returns {number}
 */
export function resolveWordWheelQuestCoins(cloudUser) {
    return readCoinsValue(readCoinsMap(cloudUser), PUZZLE_COINS_APP.WORD_WHEEL_QUEST);
}

/**
 * Web hint ledger total (`puzzleCoins.web_hint` or legacy `hint`).
 * @param {object | null | undefined} cloudUser
 * @returns {number}
 */
export function resolveWebHintPuzzleCoins(cloudUser) {
    const map = readCoinsMap(cloudUser);
    if (!map) return 0;
    if (map[PUZZLE_COINS_APP.WEB_HINT] != null) {
        return readCoinsSigned(map, PUZZLE_COINS_APP.WEB_HINT);
    }
    return readCoinsSigned(map, PUZZLE_COINS_APP.HINT);
}

/**
 * Web puzzle coins (Stripe unlocks / web hint spends) from profile.
 * @param {object | null | undefined} cloudUser
 * @returns {number}
 */
export function resolveWebPuzzleCoins(cloudUser) {
    const map = readCoinsMap(cloudUser);
    if (!map) return 0;
    if (map[PUZZLE_COINS_APP.WEB] != null) {
        return readCoinsSigned(map, PUZZLE_COINS_APP.WEB);
    }
    return readCoinsValue(map, PUZZLE_COINS_APP.PURCHASE);
}

/**
 * Combined lifetime coins (`puzzleCoins.total`, or sum of per-app keys).
 * @param {object | null | undefined} cloudUser
 * @returns {number}
 */
export function resolveTotalPuzzleCoins(cloudUser) {
    const map = readCoinsMap(cloudUser);
    if (!map) return 0;
    if (map[PUZZLE_COINS_APP.TOTAL] != null) {
        return readCoinsValue(map, PUZZLE_COINS_APP.TOTAL);
    }
    return resolveSlidingQuestCoins(cloudUser)
        + resolveJigsawMuseCoins(cloudUser)
        + resolveWordWheelQuestCoins(cloudUser)
        + resolveWebPuzzleCoins(cloudUser);
}

/**
 * Coins earned on a single completion response (`PUT …/progress` with `isCompleted: true`).
 * @param {object | null | undefined} playRo
 * @returns {number}
 */
export function readCoinsEarned(playRo) {
    const n = Number(playRo?.coinsEarned);
    return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * Running total after completion (`totalPuzzleCoins` on play response).
 * @param {object | null | undefined} playRo
 * @returns {number}
 */
export function readTotalPuzzleCoinsFromPlay(playRo) {
    const n = Number(playRo?.totalPuzzleCoins);
    return Number.isFinite(n) && n >= 0 ? n : 0;
}
